import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { getToken } from '@/lib/apiClient';
import { useMe } from '@/queries/auth.queries';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { clearCredentials, setUser } from '@/store/authSlice';
import { useTheme } from '@/hooks';
import { Login } from '@/pages/LoginPage';
import { AuthCallback } from '@/pages/AuthCallbackPage';
import { FoxitView } from '@/pages/FoxitPage';
import { StandardizerView } from '@/pages/StandardizerPage';
import { LibraryView } from '@/pages/LibraryPage';
import { AdminView } from '@/pages/AdminPage';
import { DownloadsPage } from '@/pages/DownloadsPage';
import { Layout } from '@/components/layout/AppLayout';
import { ComingSoon } from '@/components/layout/ComingSoon';

function RequirePermission({ perm, children }: { perm: string; children: React.ReactNode }) {
  const user = useAppSelector((s) => s.auth.user);
  if (user && !user.permissions.includes(perm)) return <Navigate to="/foxit" replace />;
  return <>{children}</>;
}

export function App() {
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);
  const { theme, toggle } = useTheme();
  const hasToken = Boolean(getToken());

  // The Microsoft sign-in redirect lands on /auth/callback before any session
  // exists — handle it ahead of the auth gate.
  const isAuthCallback =
    typeof window !== 'undefined' && window.location.pathname === '/auth/callback';

  // Bootstrap auth: if a token exists, hydrate the user from /auth/me into redux.
  const meQuery = useMe(hasToken && !user);

  useEffect(() => {
    if (meQuery.data) dispatch(setUser(meQuery.data));
  }, [meQuery.data, dispatch]);

  useEffect(() => {
    if (meQuery.isError) dispatch(clearCredentials());
  }, [meQuery.isError, dispatch]);

  // Consume the Microsoft sign-in redirect before anything else.
  if (isAuthCallback) {
    return <AuthCallback />;
  }

  // While hydrating an existing session, show the loading state.
  if (hasToken && !user && meQuery.isLoading) {
    return (
      <div className="grid min-h-screen place-items-center text-muted-foreground">
        <div className="flex items-center gap-3">
          <span className="w-2 h-2 rounded-full bg-primary animate-pulse-soft" />
          Loading workspace…
        </div>
      </div>
    );
  }

  // No authenticated user -> Login screen, always at the root URL (never /foxit
  // or any other protected path).
  if (!user) {
    if (window.location.pathname !== '/') window.history.replaceState(null, '', '/');
    return <Login theme={theme} onToggleTheme={toggle} />;
  }

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<Navigate to="/foxit" replace />} />
        <Route path="/foxit" element={<FoxitView />} />
        <Route path="/standardizer" element={<StandardizerView />} />
        <Route path="/downloads" element={<DownloadsPage />} />
        <Route
          path="/library"
          element={
            <RequirePermission perm="library.view">
              <LibraryView canManage={user.permissions.includes('library.manage')} />
            </RequirePermission>
          }
        />
        <Route path="/projects" element={<ComingSoon name="Projects" />} />
        <Route path="/vault" element={<ComingSoon name="Document Vault" />} />
        <Route
          path="/admin"
          element={
            <RequirePermission perm="audit.view">
              <AdminView />
            </RequirePermission>
          }
        />
        <Route path="*" element={<Navigate to="/foxit" replace />} />
      </Route>
    </Routes>
  );
}
